import React, { useContext, useEffect, useMemo, useState } from 'react'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import {
  Alert,
  Avatar,
  AvatarGroup,
  Badge,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material'
import {
  Add,
  ExpandLess,
  ExpandMore,
  Forum,
  LocalHospital,
  MyLocation,
  LocationDisabled,
  PersonAddAlt1,
  Schedule,
} from '@mui/icons-material'
import ResourcePopup from './AddResourcePopup'
import ResourceDrawer from './ResourceDrawer'
import { ResourceIcon, resolveResourceIconKey } from './resourceIcons'
import API from '../../../../services/API'
import { TenantSettingsContext } from '../../../../App'
import { ControlRoomContext } from './ControlPage'

const formatShiftTime = (value) =>
  value
    ? new Date(value).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      })
    : ''

// Worst-first summary of the crew's location-sharing consents for the card
const trackingSummary = (users = []) => {
  const states = users.map((u) => u.tracking?.status).filter(Boolean)
  if (!states.length) return null
  if (states.includes('Declined'))
    return { label: 'GPS declined', tone: '#FCA5A5', off: true }
  if (states.includes('Expired'))
    return { label: 'GPS expired', tone: '#FDBA74', off: true }
  if (states.includes('Pending'))
    return { label: 'GPS requested', tone: '#FDE68A', off: true }
  return { label: 'GPS sharing', tone: '#BBF7D0', off: false }
}

// Earliest crew shift end — flagged on the card once it's within an hour
const soonestShiftEnd = (users = []) => {
  const ends = users
    .map((u) => (u.shiftEnd ? new Date(u.shiftEnd) : null))
    .filter((d) => d && !Number.isNaN(d.getTime()))
    .sort((a, b) => a - b)
  return ends[0] ?? null
}

const statusStyles = {
  Available: { bg: '#00964C', glow: 'rgba(0,150,76,0.28)' },
  'Off Duty': { bg: '#475569', glow: 'rgba(71,85,105,0.22)' },
  'Manager On Call': { bg: '#1F3D7C', glow: 'rgba(31,61,124,0.26)' },
  'Mobile & Available': { bg: '#157746', glow: '#15774647' },
  'C1 Only': { bg: '#0e4f2e', glow: '#0e4f2e47' },
  'C1 & C2 Only': { bg: '#0e4f2e', glow: '#0e4f2e47' },
  Break: { bg: '#1F3D7C', glow: 'rgba(31,61,124,0.26)' },
  Unavailable: { bg: '#85276A', glow: 'rgba(133,39,106,0.25)' },
  Assigned: { bg: '#E32525', glow: 'rgba(227,37,37,0.26)' },
  Acknowledged: { bg: '#C2410C', glow: 'rgba(194,65,12,0.26)' },
  'On Route': { bg: '#9A3412', glow: 'rgba(154,52,18,0.24)' },
  'At Scene': { bg: '#CCA43B', glow: 'rgba(204,164,59,0.26)' },
  'On Scene': { bg: '#CCA43B', glow: 'rgba(204,164,59,0.26)' },
  'Leave Scene': { bg: '#CCA43B', glow: 'rgba(204,164,59,0.26)' },
  'At Hosp': { bg: '#CCA43B', glow: 'rgba(204,164,59,0.26)' },
  Handover: { bg: '#CCA43B', glow: 'rgba(204,164,59,0.26)' },
}

const bottomStatuses = ['Unavailable', 'Off Duty']

const clinicalRoleRanking = [
  'Doctor',
  'Nurse',
  'Independent Prescriber',
  'Paramedic',
  'Student Paramedic',
  'IHCD EMT',
  'FREC 5',
  'FREC 4',
  'FREC 3',
  'First Aid',
]

const isLowestFirstClinicalRanking = (roles = []) => {
  const firstRankedRole = roles.find((role) =>
    clinicalRoleRanking.includes(role),
  )
  const lastRankedRole = roles
    .slice()
    .reverse()
    .find((role) => clinicalRoleRanking.includes(role))

  if (!firstRankedRole || !lastRankedRole) return false

  return (
    clinicalRoleRanking.indexOf(firstRankedRole) >
    clinicalRoleRanking.indexOf(lastRankedRole)
  )
}

const normaliseClinicalRoleRanking = (roles = []) => {
  const rankedRoles = roles
    .filter((role) => clinicalRoleRanking.includes(role))
    .sort(
      (a, b) => clinicalRoleRanking.indexOf(a) - clinicalRoleRanking.indexOf(b),
    )
  const unrankedRoles = roles.filter(
    (role) => !clinicalRoleRanking.includes(role),
  )

  return [...rankedRoles, ...unrankedRoles]
}

const ResourceCard = ({
  resource,
  setSelectedResource,
  unreadMessages = 0,
  onOpenChat,
}) => {
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig
  const style = statusStyles[resource?.status] || {
    bg: '#268EBB',
    glow: 'rgba(38,142,187,0.24)',
  }
  // Flash while the crew hasn't acknowledged the tasking on their device —
  // the mobile acknowledgement moves the status Assigned → Acknowledged
  const awaitingAcknowledgement = resource?.status === 'Assigned'
  // Breaks no longer auto-end — pulse blue until the dispatcher ends it
  const breakExpired =
    resource?.status === 'Break' &&
    resource?.breakUntil &&
    new Date(resource.breakUntil).getTime() <= Date.now()
  const rankedRoles = isLowestFirstClinicalRanking(orgSettings?.roles)
    ? normaliseClinicalRoleRanking(orgSettings.roles)
    : orgSettings?.roles

  const highestClinicalRole = rankedRoles?.find((role) =>
    resource?.users?.some((user) => user.clinicalRole === role),
  )
  const breakMsRemaining = resource?.breakUntil
    ? new Date(resource.breakUntil).getTime() - Date.now()
    : 0
  const breakSecondsRemaining = Math.max(0, Math.ceil(breakMsRemaining / 1000))
  const breakMinutes = Math.floor(breakSecondsRemaining / 60)
  const breakSeconds = breakSecondsRemaining % 60
  const breakCountdown =
    resource?.status === 'Break' && resource?.breakUntil
      ? `${breakMinutes}:${String(breakSeconds).padStart(2, '0')}`
      : null

  const tracking = trackingSummary(resource?.users)
  const shiftEnd = soonestShiftEnd(resource?.users)
  const shiftEndMs = shiftEnd ? shiftEnd.getTime() - Date.now() : null
  const shiftEndingSoon = shiftEndMs !== null && shiftEndMs <= 60 * 60 * 1000
  const shiftEnded = shiftEndMs !== null && shiftEndMs <= 0

  return (
    <Paper
      elevation={0}
      onClick={() => setSelectedResource(resource)}
      sx={{
        borderRadius: 3,
        overflow: 'hidden',
        cursor: 'pointer',
        color: 'white',
        backgroundColor: style.bg,
        backgroundImage:
          'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 45%, rgba(0,0,0,0.12) 100%)',
        boxShadow: `0 10px 26px ${style.glow}`,
        border: '1px solid rgba(255,255,255,0.12)',
        transition: 'all 0.18s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 16px 34px ${style.glow}`,
        },
        ...(awaitingAcknowledgement && {
          animation: 'resourceAwaitingAck 1.1s ease-in-out infinite',
          '@keyframes resourceAwaitingAck': {
            '0%, 100%': {
              filter: 'brightness(1)',
              boxShadow: `0 10px 26px ${style.glow}`,
            },
            '50%': {
              filter: 'brightness(1.45)',
              boxShadow:
                '0 0 0 6px rgba(227,37,37,0.38), 0 10px 30px rgba(227,37,37,0.55)',
            },
          },
        }),
        ...(breakExpired && {
          animation: 'resourceBreakOver 1.4s ease-in-out infinite',
          '@keyframes resourceBreakOver': {
            '0%, 100%': {
              filter: 'brightness(1)',
              boxShadow: `0 10px 26px ${style.glow}`,
            },
            '50%': {
              filter: 'brightness(1.35)',
              boxShadow:
                '0 0 0 6px rgba(37,99,235,0.4), 0 10px 30px rgba(37,99,235,0.55)',
            },
          },
        }),
      }}
    >
      <Grid container alignItems="center" spacing={1} sx={{ p: 1.25 }}>
        <Grid item xs={12} md={4} lg={4}>
          <Stack>
            <Stack direction="row" spacing={0.6} alignItems="center">
              <ResourceIcon
                iconKey={resolveResourceIconKey(resource)}
                size={14}
                color="rgba(255,255,255,0.85)"
              />
              <Typography variant="subtitle2" noWrap sx={{ opacity: 0.85 }}>
                {resource.type || 'Other'}
              </Typography>
            </Stack>
            <Tooltip title={resource.callSign} enterDelay={500}>
              <Typography
                fontWeight={900}
                color="white"
                noWrap
                sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
              >
                {resource.callSign}
              </Typography>
            </Tooltip>
          </Stack>
        </Grid>

        <Grid item xs={6} md={2.5} lg={4.5}>
          <Stack direction="row" gap={0.75} flexWrap="wrap">
            <Chip
              label={resource.status || 'Unknown'}
              size="small"
              sx={{
                borderRadius: 999,
                backgroundColor: 'rgba(255,255,255,0.16)',
                color: 'white',
                fontWeight: 800,
              }}
            />
            {breakCountdown && (
              <Chip
                label={breakCountdown}
                size="small"
                sx={{
                  borderRadius: 999,
                  backgroundColor: 'rgba(255,255,255,0.24)',
                  color: 'white',
                  fontWeight: 900,
                  fontVariantNumeric: 'tabular-nums',
                }}
              />
            )}
            {tracking && (
              <Tooltip
                title={`${tracking.label} — set per person in the resource drawer`}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 24,
                    height: 24,
                    borderRadius: 999,
                    backgroundColor: 'rgba(255,255,255,0.16)',
                  }}
                >
                  {tracking.off ? (
                    <LocationDisabled
                      sx={{ fontSize: 15, color: tracking.tone }}
                    />
                  ) : (
                    <MyLocation sx={{ fontSize: 15, color: tracking.tone }} />
                  )}
                </Box>
              </Tooltip>
            )}
            {shiftEndingSoon && (
              <Tooltip
                title={
                  shiftEnded
                    ? 'A crew member’s shift has ended'
                    : 'A crew member’s shift ends within the hour'
                }
              >
                <Chip
                  icon={
                    <Schedule
                      sx={{
                        fontSize: 14,
                        color: `${shiftEnded ? '#FCA5A5' : '#FDE68A'} !important`,
                      }}
                    />
                  }
                  label={
                    shiftEnded
                      ? `Shift ended ${formatShiftTime(shiftEnd)}`
                      : `Shift ends ${formatShiftTime(shiftEnd)}`
                  }
                  size="small"
                  sx={{
                    borderRadius: 999,
                    backgroundColor: shiftEnded
                      ? 'rgba(227,37,37,0.28)'
                      : 'rgba(204,164,59,0.28)',
                    color: shiftEnded ? '#FECACA' : '#FEF3C7',
                    fontWeight: 800,
                  }}
                />
              </Tooltip>
            )}
          </Stack>
        </Grid>

        <Grid item xs={12} md={4} lg={3.5}>
          <Stack
            direction="row"
            gap={1}
            alignItems="center"
            justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
          >
            {highestClinicalRole && (
              <Chip
                label={highestClinicalRole}
                size="small"
                variant="outlined"
                sx={{
                  borderRadius: 999,
                  backgroundColor: 'rgba(255,255,255,0.16)',
                  color: 'white',
                  fontWeight: 800,
                }}
              />
            )}
            <AvatarGroup max={4}>
              {resource?.users?.map((u) => (
                <Tooltip
                  key={u._id || u.id}
                  title={`${u.name} (${u.clinicalRole})`}
                >
                  <Avatar
                    sx={{
                      width: 24,
                      height: 24,
                      border: '2px solid rgba(255,255,255,0.75)',
                    }}
                    src={u?.photo}
                  >
                    {u?.name?.[0]}
                  </Avatar>
                </Tooltip>
              ))}
            </AvatarGroup>

            <Tooltip title="Message this resource">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation()
                  onOpenChat?.(resource)
                }}
                sx={{
                  color: 'white',
                  backgroundColor: 'rgba(255,255,255,0.16)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.28)' },
                }}
              >
                <Badge
                  color="error"
                  badgeContent={unreadMessages}
                  overlap="circular"
                >
                  <Forum fontSize="small" />
                </Badge>
              </IconButton>
            </Tooltip>
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  )
}

const Resources = ({
  controlRoomID,
  resources = [],
  gridProps = {},
  compact = false,
}) => {
  const [openResourcePopup, setOpenResourcePopup] = useState(null)
  const [selectedResource, setSelectedResource] = useState(null)
  const [expanded, setExpanded] = useState(true)
  const [, setCountdownTick] = useState(0)
  const {
    chatSummaries = [],
    chatUnreadTotal: totalUnread = 0,
    openResourceChat,
    onShiftUnassigned = [],
    refresh,
    canDispatch = true,
  } = useContext(ControlRoomContext) ?? {}
  const [assignMenu, setAssignMenu] = useState(null)
  const [assigningUserID, setAssigningUserID] = useState(null)
  const [assignError, setAssignError] = useState('')

  const assignPersonToResource = (person, resource) => {
    setAssignMenu(null)
    setAssigningUserID(person.userID)
    setAssignError('')
    API.post('/commandcontrol/assignusertoresource', {
      controlRoomID,
      resourceID: resource.id,
      user: person.userID,
    })
      .then((res) => {
        if (res.status === 200) refresh?.()
        else
          setAssignError(
            res.data?.error || `Could not assign ${person.name}. Try again.`,
          )
      })
      .finally(() => setAssigningUserID(null))
  }

  const unreadByResource = useMemo(() => {
    const map = new Map()
    chatSummaries.forEach((summary) =>
      map.set(String(summary.resource), summary.unreadFromCrew ?? 0),
    )
    return map
  }, [chatSummaries])

  const openChatForResource = (resource) => openResourceChat?.(resource)

  // Keep the open drawer live for the fields that change remotely (crew list,
  // tracking consents, shift ends, status). Only those are merged — fields the
  // drawer edits locally (name, note, type, map position) keep their state,
  // and identity is preserved when nothing changed so the drawer's effects
  // don't re-fire on every poll.
  useEffect(() => {
    setSelectedResource((prev) => {
      if (!prev) return prev
      const fresh = resources.find(
        (r) => String(r.id ?? r._id) === String(prev.id ?? prev._id),
      )
      if (!fresh) return prev
      const liveFields = {
        users: fresh.users,
        status: fresh.status,
        breakUntil: fresh.breakUntil,
        assignedIncident: fresh.assignedIncident,
      }
      const changed =
        JSON.stringify({
          users: prev.users,
          status: prev.status,
          breakUntil: prev.breakUntil,
          assignedIncident: prev.assignedIncident,
        }) !== JSON.stringify(liveFields)
      return changed ? { ...prev, ...liveFields } : prev
    })
  }, [resources])

  useEffect(() => {
    if (!resources.some((resource) => resource.status === 'Break')) {
      return undefined
    }

    const interval = setInterval(() => {
      setCountdownTick((tick) => tick + 1)
    }, 1000)

    return () => clearInterval(interval)
  }, [resources])

  const sortedResources = useMemo(() => {
    return [...resources].sort((a, b) => {
      const aBottom = bottomStatuses.includes(a.status)
      const bBottom = bottomStatuses.includes(b.status)

      if (aBottom !== bBottom) {
        return aBottom ? 1 : -1
      }

      return (a.callSign || '').localeCompare(b.callSign || '', undefined, {
        numeric: true,
        sensitivity: 'base',
      })
    })
  }, [resources])

  const availableCount = useMemo(
    () => resources.filter((r) => r.status === 'Available').length,
    [resources],
  )

  const activeCount = useMemo(
    () =>
      resources.filter((r) => ![...bottomStatuses, 'Break'].includes(r.status))
        .length,
    [resources],
  )

  return (
    <Grid item xs={12} lg={6} {...gridProps}>
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 4,
          overflow: 'hidden',
          border: '1px solid rgba(255,255,255,0.12)',
          background: 'rgba(255,255,255,0.96)',
          boxShadow: '0 16px 42px rgba(0,0,0,0.22)',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          gap={1.5}
          sx={{
            p: 2,
            background:
              'linear-gradient(135deg, #242F40 0%, #2c3e50 70%, #239AA4 160%)',
            color: 'white',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255,255,255,0.12)',
                flexShrink: 0,
              }}
            >
              <LocalHospital />
            </Box>

            <Stack spacing={0}>
              <Typography fontWeight={900} color="white">
                Resources
              </Typography>
              <Typography
                variant="caption"
                sx={{ opacity: 0.72, color: 'white' }}
              >
                {resources.length} operational resources
              </Typography>
            </Stack>
          </Stack>

          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent={{ xs: 'space-between', sm: 'flex-end' }}
            flexWrap="wrap"
            useFlexGap
          >
            <Chip
              size="small"
              label={`${availableCount} available`}
              sx={{
                borderRadius: 999,
                backgroundColor: 'rgba(0,150,76,0.18)',
                color: '#BBF7D0',
                fontWeight: 900,
                border: '1px solid rgba(187,247,208,0.24)',
              }}
            />

            <Chip
              size="small"
              label={`${activeCount}/${resources.length} active`}
              sx={{
                borderRadius: 999,
                backgroundColor: 'rgba(255,255,255,0.12)',
                color: 'white',
                fontWeight: 800,
                border: '1px solid rgba(255,255,255,0.14)',
              }}
            />

            <Tooltip title="Resource messages">
              <IconButton
                size="small"
                onClick={() => openChatForResource(null)}
                sx={{
                  color: 'white',
                  backgroundColor: 'rgba(255,255,255,0.12)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
                }}
              >
                <Badge
                  color="error"
                  badgeContent={totalUnread}
                  overlap="circular"
                >
                  <Forum fontSize="small" />
                </Badge>
              </IconButton>
            </Tooltip>

            {canDispatch && (
              <IconButton
                size="small"
                onClick={() => setOpenResourcePopup('New')}
                sx={{
                  color: 'white',
                  backgroundColor: 'rgba(255,255,255,0.12)',
                  '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
                }}
              >
                <Add />
              </IconButton>
            )}

            <IconButton
              size="small"
              onClick={() => setExpanded((prev) => !prev)}
              sx={{
                color: 'white',
                backgroundColor: 'rgba(255,255,255,0.12)',
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.2)' },
              }}
            >
              {expanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Stack>
        </Stack>

        <Collapse
          in={expanded}
          timeout="auto"
          unmountOnExit
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            '& .MuiCollapse-wrapper, & .MuiCollapse-wrapperInner': {
              height: '100%',
              minHeight: 0,
            },
          }}
        >
          <Stack
            spacing={1.25}
            sx={{
              p: 2,
              flex: 1,
              minHeight: 0,
              height: '100%',
              overflow: 'auto',
            }}
          >
            {resources.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No resources added yet.
              </Typography>
            ) : (
              sortedResources.map((r, index) => (
                <ResourceCard
                  resource={r}
                  key={r.id || r._id || index}
                  setSelectedResource={setSelectedResource}
                  unreadMessages={
                    unreadByResource.get(String(r._id ?? r.id)) ?? 0
                  }
                  onOpenChat={openChatForResource}
                />
              ))
            )}

            {assignError && (
              <Alert
                severity="error"
                onClose={() => setAssignError('')}
                sx={{ borderRadius: 3 }}
              >
                {assignError}
              </Alert>
            )}

            {onShiftUnassigned.length > 0 && (
              <>
                <Divider sx={{ pt: 0.5 }}>
                  <Typography
                    variant="caption"
                    sx={{ fontWeight: 900, color: '#64748B' }}
                  >
                    ON SHIFT — NOT ASSIGNED TO A RESOURCE
                  </Typography>
                </Divider>

                {onShiftUnassigned.map((person) => (
                  <Paper
                    key={person.userID}
                    elevation={0}
                    sx={{
                      borderRadius: 3,
                      p: 1.25,
                      border: '1px dashed #CBD5E1',
                      backgroundColor: '#F8FAFC',
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={1}
                    >
                      <Stack direction="row" spacing={1.25} alignItems="center">
                        <Avatar
                          src={person.photo || undefined}
                          sx={{ width: 30, height: 30 }}
                        >
                          {person.name?.[0]}
                        </Avatar>
                        <Stack>
                          <Typography fontWeight={800} color="#242F40">
                            {person.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {[
                              person.clinicalRole,
                              person.eventAssignment,
                              `${formatShiftTime(person.shiftStart)}–${formatShiftTime(person.shiftEnd)}`,
                              person.clockedIn
                                ? 'Clocked in'
                                : 'Not clocked in',
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </Typography>
                        </Stack>
                      </Stack>

                      <Tooltip title="Assign to a resource">
                        <span>
                          <IconButton
                            size="small"
                            disabled={
                              !canDispatch ||
                              !resources.length ||
                              assigningUserID === person.userID
                            }
                            onClick={(e) =>
                              setAssignMenu({
                                anchor: e.currentTarget,
                                person,
                              })
                            }
                            sx={{
                              backgroundColor: '#E6F7F9',
                              color: '#1E7F88',
                              '&:hover': { backgroundColor: '#D7F1F4' },
                            }}
                          >
                            <PersonAddAlt1 fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Paper>
                ))}

                <Menu
                  anchorEl={assignMenu?.anchor}
                  open={!!assignMenu}
                  onClose={() => setAssignMenu(null)}
                >
                  {sortedResources.map((r) => (
                    <MenuItem
                      key={r.id || r._id}
                      onClick={() =>
                        assignPersonToResource(assignMenu.person, r)
                      }
                    >
                      {r.callSign} — {r.type || 'Other'}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}
          </Stack>
        </Collapse>
      </Paper>

      <ResourcePopup
        resource={openResourcePopup}
        controlRoomID={controlRoomID}
        open={!!openResourcePopup}
        onClose={() => setOpenResourcePopup(null)}
      />

      <ResourceDrawer
        selectedResource={selectedResource}
        setSelectedResource={setSelectedResource}
        controlRoomID={controlRoomID}
        onOpenChat={openChatForResource}
      />
    </Grid>
  )
}

export default Resources
